// OP-3605 — Regras puras da tela do equipamento. Os quatro eixos da peça
// (`domain/estoque.ts`) decidem o que a tela oferece: manutenção
// (`ORIGEM_VALIDA`/`EXIGE_INTEGRIDADE`), retorno (`condicao EM_MANUTENCAO`) e
// proposta de baixa (`podeProporBaixa`). Gates visuais; quem recusa é o servidor.
import { Condicao, HistoricoUnidadeRow, NivelCerteza, Situacao, UnidadeDetalhe, CicloVida, lugarDoLocal, rotuloTipoLocal } from '../../../../types/estoque'

export const SITUACAO_UI: Record<Situacao, { bg: string; color: string; label: string }> = {
  EM_ESTOQUE: { bg: '#D1FAE5', color: '#065F46', label: 'Em estoque' },
  SEPARADO: { bg: '#E0E7FF', color: '#3730A3', label: 'Separado' },
  EM_TRANSITO: { bg: '#DBEAFE', color: '#1E3A8A', label: 'Em trânsito' },
  EM_CAMPO: { bg: '#FEF3C7', color: '#92400E', label: 'Em campo' },
  AGUARDANDO_RETORNO: { bg: '#FED7AA', color: '#9A3412', label: 'Aguardando retorno' },
  EM_CONFERENCIA: { bg: '#FEF3C7', color: '#92400E', label: 'Em conferência' },
}

export const CONDICAO_UI: Record<Condicao, { bg: string; color: string; label: string }> = {
  OK: { bg: '#D1FAE5', color: '#065F46', label: 'OK' },
  AVARIADO: { bg: '#FEE2E2', color: '#991B1B', label: 'Avariado' },
  EM_MANUTENCAO: { bg: '#DBEAFE', color: '#1E3A8A', label: 'Em manutenção' },
  SUCATA: { bg: '#FEE2E2', color: '#991B1B', label: 'Sucata' },
}

export const CICLO_UI: Record<CicloVida, { bg: string; color: string; label: string }> = {
  ATIVO: { bg: '#D1FAE5', color: '#065F46', label: 'Ativo' },
  BLOQUEADO: { bg: '#FEF3C7', color: '#92400E', label: 'Bloqueado' },
  EXTRAVIADO: { bg: '#FEE2E2', color: '#991B1B', label: 'Extraviado' },
  BAIXADO: { bg: '#F3F4F6', color: '#374151', label: 'Baixado' },
}

export const NIVEL_UI: Record<NivelCerteza, { bg: string; color: string; label: string; explica: string }> = {
  CONFIRMADO: { bg: '#D1FAE5', color: '#065F46', label: 'Confirmado', explica: 'A própria peça imprimiu bilhete no lugar em que o razão diz que ela está.' },
  CORROBORADO: { bg: '#DBEAFE', color: '#1E3A8A', label: 'Corroborado', explica: 'A peça não deu sinal, mas o ponto está vendendo. O local está vivo.' },
  DECLARADO: { bg: '#F3F4F6', color: '#374151', label: 'Declarado', explica: 'Só o razão afirma onde a peça está: não há venda que confirme.' },
  CONTRADITADO: { bg: '#FEE2E2', color: '#991B1B', label: 'Contraditado', explica: 'A venda aponta outro lugar ou outro produtor. O razão e a venda discordam, e alguém precisa decidir qual corrigir.' },
}

/**
 * As duas guardas que `validarMovimento` aplica a QUALQUER movimento de peça,
 * antes de olhar situação ou tipo: baixada não se move, e sucateada só aceita
 * BAIXA. Valem para manutenção e valeriam para qualquer ação nova aqui.
 *
 * A segunda é o caso que aparece sozinho na operação: "voltou sem conserto"
 * marca a peça SUCATA na praça, e a tela passava a oferecer um novo envio para
 * manutenção que o servidor recusa com `UNIDADE_SUCATA`.
 */
export function impedimentoMovimentoDaPeca(u: Pick<UnidadeDetalhe, 'condicao' | 'cicloVida'>): string | null {
  if (u.cicloVida === 'BAIXADO') return 'A peça saiu do patrimônio: nenhum movimento a aceita.'
  if (u.condicao === 'SUCATA') return 'A peça está como sucata: o único caminho é a baixa.'
  return null
}

/** A peça ainda é patrimônio sobre o qual se age. */
export const ehPatrimonioVivo = (u: Pick<UnidadeDetalhe, 'cicloVida'>): boolean =>
  u.cicloVida !== 'BAIXADO'

/**
 * Enviar para manutenção: as guardas gerais acima, mais
 * `ORIGEM_VALIDA.SAIDA_MANUTENCAO` (EM_ESTOQUE ou EM_CONFERENCIA). A peça já
 * em manutenção não vai de novo. Fornecedor é obrigatório no servidor.
 *
 * Extraviada NÃO é impedimento aqui: o domínio só barra BAIXADO e SUCATA para
 * movimento — a peça que reaparece pode ir para a assistência.
 */
export function impedimentoManutencao(u: Pick<UnidadeDetalhe, 'situacao' | 'condicao' | 'cicloVida'>): string | null {
  const geral = impedimentoMovimentoDaPeca(u)
  if (geral) return geral
  if (u.condicao === 'EM_MANUTENCAO') return 'A peça já está em manutenção.'
  if (['EM_ESTOQUE', 'EM_CONFERENCIA'].indexOf(u.situacao) < 0) {
    return 'Só sai para manutenção o que está na praça (em estoque ou em conferência).'
  }
  return null
}

/** Retorno: só volta o que o razão registra como em manutenção. */
export function impedimentoRetornoManutencao(u: Pick<UnidadeDetalhe, 'condicao' | 'cicloVida'>): string | null {
  if (u.cicloVida === 'BAIXADO') return 'A peça saiu do patrimônio: nenhum movimento a aceita.'
  return u.condicao === 'EM_MANUTENCAO' ? null : 'Esta peça não está em manutenção.'
}

/**
 * Propor baixa: espelho de `podeProporBaixa` — uma proposta ABERTA por peça.
 * Recusada fica no histórico e não impede outra; a alçada é do servidor.
 */
export function impedimentoProporBaixa(u: Pick<UnidadeDetalhe, 'cicloVida' | 'baixaPropostaId'>): string | null {
  if (u.cicloVida === 'BAIXADO') return 'A peça já saiu do patrimônio.'
  if (u.baixaPropostaId != null) return 'Já existe uma proposta de baixa aberta para esta peça.'
  return null
}

export function extravioParaReaparecer(historico: HistoricoUnidadeRow[] | null | undefined): HistoricoUnidadeRow | null {
  const ultimo = (historico ?? [])[0]
  return ultimo && ultimo.tipo === 'EXTRAVIO' && ultimo.estornadoPorId == null ? ultimo : null
}

export function impedimentoReaparecimento(c: {
  cicloVida: CicloVida
  podeEstornar: boolean
  baixaPropostaId: number | null | undefined
  extravio: HistoricoUnidadeRow | null
  motivo: string
}): string | null {
  if (c.cicloVida !== 'EXTRAVIADO') return 'A peça não está extraviada.'
  if (!c.podeEstornar) return 'Registrar o reaparecimento exige a permissão "Estornar movimento".'
  if (c.baixaPropostaId != null) return 'Há uma proposta de baixa aberta para esta peça: a gerência precisa recusá-la antes.'
  if (!c.extravio) return 'A peça teve lançamento depois do extravio, e o estorno só desfaz o último. Fale com a coordenação para corrigir pelo razão.'
  if (!c.motivo.trim()) return 'Escreva onde e como a peça apareceu: o motivo fica no razão.'
  return null
}

/** Quem é o detentor, em uma frase: o razão responde pelo lugar. */
export function ondeEsta(u: Pick<UnidadeDetalhe, 'localNome' | 'localTipo' | 'localBaseNome'>): string {
  const base = `base ${u.localBaseNome}`
  if (u.localTipo === 'PRACA') return `${u.localNome} (${base})`
  if (u.localTipo === 'TRANSITO') return `${lugarDoLocal(u.localNome, u.localTipo)} (${base})`
  return `${u.localNome} — ${rotuloTipoLocal(u.localTipo)} (${base})`
}

export function ficouDepois(
  h: Pick<HistoricoUnidadeRow, 'situacaoApos' | 'condicaoApos' | 'cicloVidaApos'>,
  anterior?: Pick<HistoricoUnidadeRow, 'cicloVidaApos'> | null,
): string | null {
  const ciclo = h.cicloVidaApos ? CICLO_UI[h.cicloVidaApos]?.label ?? h.cicloVidaApos : null
  if (h.cicloVidaApos === 'EXTRAVIADO' || h.cicloVidaApos === 'BAIXADO') return ciclo
  const mudou = !!anterior?.cicloVidaApos && anterior.cicloVidaApos !== h.cicloVidaApos
  const partes = [
    h.situacaoApos ? SITUACAO_UI[h.situacaoApos]?.label ?? h.situacaoApos : null,
    h.condicaoApos ? CONDICAO_UI[h.condicaoApos]?.label ?? h.condicaoApos : null,
    h.cicloVidaApos !== 'ATIVO' || mudou ? ciclo : null,
  ].filter(Boolean)
  return partes.length ? partes.join(' · ') : null
}

export function baseNaLista(
  u: Pick<UnidadeDetalhe, 'localId' | 'localBaseId' | 'localBaseNome'>,
  pracaDaLista: number | null,
): string | null {
  if (pracaDaLista != null && Number(u.localBaseId) === Number(pracaDaLista) && Number(u.localId) !== Number(pracaDaLista)) return 'base aqui'
  if (Number(u.localBaseId) !== Number(u.localId)) return `base ${u.localBaseNome}`
  return null
}

export interface ChipDaPeca {
  bg: string
  color: string
  label: string
}

export interface LeituraDaPeca {
  /** O que a peça é agora, em uma ou duas pílulas — nunca três em contradição. */
  chips: ChipDaPeca[]
  /** O que as outras dimensões diziam, como contexto e não como estado. */
  contexto: string | null
}

/**
 * OP-3619 — a peça extraviada dizia "Em campo · OK · Extraviado".
 *
 * Os três eixos são corretos separadamente e mentem juntos: uma peça
 * extraviada não está em campo — ninguém sabe onde ela está —, e a condição
 * que se guarda é a da última vez em que alguém a viu.
 *
 * Por isso a precedência é de EXTRAVIADO e BAIXADO, e só deles: nesses dois a
 * peça saiu do lugar que o razão dizia, então o ciclo manda e situação e
 * condição viram contexto. BLOQUEADO é outra coisa — a peça continua onde
 * está, apenas não pode ser usada —, e ali os três eixos convivem sem mentir.
 */
const NEUTRO = { bg: '#F3F4F6', color: '#374151' }

/** Valor fora do mapa vira pílula neutra com o próprio código: nada some. */
const chipDe = (mapa: Record<string, ChipDaPeca>, valor: string): ChipDaPeca => mapa[valor] ?? { ...NEUTRO, label: valor }

export function leituraDaPeca(u: Pick<UnidadeDetalhe, 'situacao' | 'condicao' | 'cicloVida' | 'localNome'>): LeituraDaPeca {
  const situacao = chipDe(SITUACAO_UI, u.situacao)
  const condicao = chipDe(CONDICAO_UI, u.condicao)
  const ciclo = chipDe(CICLO_UI, u.cicloVida)

  if (u.cicloVida === 'EXTRAVIADO' || u.cicloVida === 'BAIXADO') {
    const partes: string[] = []
    if (u.localNome) partes.push(`último local conhecido: ${u.localNome}`)
    partes.push(`condição na saída: ${condicao.label}`)
    return { chips: [ciclo], contexto: partes.join(' · ') }
  }

  const chips = [situacao, condicao]
  if (u.cicloVida !== 'ATIVO') chips.push(ciclo)
  return { chips, contexto: null }
}
